/**
 * HealthKit sync utility for iOS (Capacitor + @capgo/capacitor-health)
 *
 * Syncs workouts, runs, nutrition, and weight to Apple Health.
 * All functions gracefully no-op on web / Android.
 *
 * On iOS, Apple Health shows:
 *  - Active calories burned from workouts in the Activity ring
 *  - Body weight in the Health app body measurements
 *  - Nutrition macros in the Health app nutrition section
 */

let _plugin = null;

async function getPlugin() {
  if (_plugin !== null) return _plugin;
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) {
      _plugin = false;
      return false;
    }
    const { Health } = await import("@capgo/capacitor-health");
    const { available } = await Health.isAvailable();
    _plugin = available ? Health : false;
    return _plugin;
  } catch {
    _plugin = false;
    return false;
  }
}

/**
 * Request HealthKit authorization for all data types Elevate uses.
 * Safe to call multiple times — iOS will only prompt once.
 * Call this at app startup after the user is signed in.
 * Also requests nutrition write permissions via the custom ElevateHealth plugin.
 */
export async function requestHealthAuthorization() {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    await plugin.requestAuthorization({
      read: ["calories", "totalCalories", "weight", "steps", "heartRate", "distance"],
      write: ["calories", "weight"],
    });
    // Also pre-authorize nutrition macro writes (custom plugin, no-op on web)
    const { requestNutritionAuthorization } = await import("./elevate-health-plugin.js");
    await requestNutritionAuthorization().catch((e) => console.error("Nutrition auth failed:", e));
    return true;
  } catch {
    return false;
  }
}

/**
 * Sync active calories burned from a completed workout to Apple Health.
 * Shows in the Move ring on Apple Watch / Activity app.
 *
 * @param {Object} workout - Elevate workout object with startTime, endTime, caloriesBurned
 */
export async function syncWorkoutToHealth(workout) {
  const plugin = await getPlugin();
  if (!plugin) return;
  if (!workout.caloriesBurned || workout.caloriesBurned <= 0) return;

  try {
    await plugin.saveSample({
      dataType: "calories",
      value: workout.caloriesBurned,
      unit: "kilocalorie",
      startDate: new Date(workout.startTime).toISOString(),
      endDate: new Date(workout.endTime || Date.now()).toISOString(),
      metadata: { source: "Elevate", workoutName: workout.name || "Workout" },
    });
  } catch (e) {
    console.warn("HealthKit workout sync failed:", e);
  }
}

/**
 * Sync active calories burned from a completed run to Apple Health.
 *
 * @param {Object} run - Elevate run object with timestamp, duration (min), caloriesBurned
 */
export async function syncRunToHealth(run) {
  const plugin = await getPlugin();
  if (!plugin) return;
  if (!run.caloriesBurned || run.caloriesBurned <= 0) return;

  try {
    const startDate = new Date(run.timestamp).toISOString();
    const durationMs = (run.duration || 0) * 60 * 1000;
    const endDate = new Date(run.timestamp + durationMs).toISOString();

    await plugin.saveSample({
      dataType: "calories",
      value: run.caloriesBurned,
      unit: "kilocalorie",
      startDate,
      endDate,
      metadata: { source: "Elevate", workoutName: "Run" },
    });
  } catch (e) {
    console.warn("HealthKit run sync failed:", e);
  }
}

/**
 * Sync a body weight entry to Apple Health.
 * Elevate stores weight in lbs.
 *
 * @param {number} weightLbs - Weight in pounds
 */
export async function syncWeightToHealth(weightLbs) {
  const plugin = await getPlugin();
  if (!plugin) return;
  if (!weightLbs || weightLbs <= 0) return;

  try {
    const kg = weightLbs * 0.453592;
    await plugin.saveSample({
      dataType: "weight",
      value: kg,
      unit: "kilogram",
      startDate: new Date().toISOString(),
      metadata: { source: "Elevate" },
    });
  } catch (e) {
    console.warn("HealthKit weight sync failed:", e);
  }
}
